import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { Router } from '@angular/router';
import { environment } from '../../../../environment/environment';
import { LoadDataParams } from '../../../../interfaces/DataSource/DataSourceParams.interface';
import { EventCateringApprovalListItemDTO } from '../../../../dto/event/event-catering-approval-list-item.dto';
import {
  getEventTypeLabelDa,
  getEventTypeMaterialIcon,
} from '../../../../utils/event-type-display.util';

export { getEventTypeLabelDa, getEventTypeMaterialIcon };

/** Narrow icon-button columns (see dynamic-table .button-column.column-fit-content). */
export const COMPACT_ICON_ACTION_COLUMN = {
  alignment: 'center' as const,
  fitContent: true,
  width: '1%',
  maxWidth: '52px',
};

export const CATERING_EVENT_LIST_TABLE_BASE = {
  pageable: true,
  sortable: true,
  rowHover: true,
  rowClickable: true,
  searchable: true,
  tableLayout: 'auto' as const,
  defaultSortColumn: 'start_date',
  defaultSortDirection: 'asc' as const,
  onRowClick: (row: EventCateringApprovalListItemDTO) => {
    openCateringPreferencesDialog(row);
  },
};

let cateringPreferencesOpenGuard: { eventId: number; at: number } | null = null;

export function openCateringPreferencesDialog(row: EventCateringApprovalListItemDTO): void {
  const eventId = row.event_id;
  const now = Date.now();
  if (
    cateringPreferencesOpenGuard &&
    cateringPreferencesOpenGuard.eventId === eventId &&
    now - cateringPreferencesOpenGuard.at < 400
  ) {
    return;
  }
  cateringPreferencesOpenGuard = { eventId, at: now };
  window.dispatchEvent(
    new CustomEvent('duna-catering-preferences', { detail: row })
  );
}

export function buildCateringPreferencesIconColumn() {
  return {
    key: 'view_preferences',
    header: '',
    type: 'icon-button' as const,
    icon: 'restaurant',
    buttonText: 'Forplejningsønsker',
    buttonColor: 'accent',
    ...COMPACT_ICON_ACTION_COLUMN,
    action: (row: EventCateringApprovalListItemDTO) => {
      openCateringPreferencesDialog(row);
    },
  };
}

export function buildEventViewIconColumn(
  router: Router,
  options?: { showIf?: (row: { guid: string }) => boolean }
) {
  return {
    key: 'view_event',
    header: '',
    type: 'icon-button' as const,
    icon: 'open_in_new',
    buttonText: 'Vis event',
    buttonColor: 'primary',
    ...COMPACT_ICON_ACTION_COLUMN,
    ...(options?.showIf ? { showIf: options.showIf } : {}),
    action: (row: { guid: string }) => {
      const url = router.serializeUrl(
        router.createUrlTree(['/event', row.guid])
      );
      window.open(url, '_blank');
    },
  };
}

export function buildCateringApproveIconColumn() {
  return {
    key: 'actions',
    header: '',
    type: 'icon-button' as const,
    icon: 'check_circle',
    buttonText: 'Godkend',
    buttonColor: 'primary',
    ...COMPACT_ICON_ACTION_COLUMN,
    action: (row: EventCateringApprovalListItemDTO) => {
      window.dispatchEvent(new CustomEvent('duna-catering-approve', { detail: row }));
    },
  };
}

export function formatCateringEventTypeDa(v: string): string {
  return getEventTypeLabelDa(v);
}

const COMPACT_NUMERIC_COLUMN = {
  type: 'text' as const,
  sortable: true,
  alignment: 'right' as const,
  fitContent: true,
  minWidth: '44px',
  maxWidth: '60px',
};

function parseEventDate(value: string | null | undefined): Date | null {
  if (value == null || value === '') return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatDaShortDate(d: Date): string {
  return d.toLocaleDateString('da-DK', {
    day: 'numeric',
    month: 'short',
  });
}

function formatDaTime(d: Date): string {
  return d.toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' });
}

function formatEventScheduleRange(row: {
  start_date?: string | null;
  end_date?: string | null;
}): string {
  const start = parseEventDate(row.start_date);
  const end = parseEventDate(row.end_date);
  if (!start) return '—';
  if (!end) {
    return `${formatDaShortDate(start)} ${formatDaTime(start)}`;
  }

  const sameDay = start.toDateString() === end.toDateString();
  if (sameDay) {
    return `${formatDaShortDate(start)} ${formatDaTime(start)}–${formatDaTime(end)}`;
  }

  return `${formatDaShortDate(start)} ${formatDaTime(start)} – ${formatDaShortDate(end)} ${formatDaTime(end)}`;
}

/** Schedule text only (event type shown via inline icon in table). */
export function formatEventScheduleOneLine(row: {
  start_date?: string | null;
  end_date?: string | null;
  event_type?: string | null;
}): string {
  return formatEventScheduleRange(row);
}

export function formatEventScheduleTooltip(row: {
  start_date?: string | null;
  end_date?: string | null;
  event_type?: string | null;
}): string {
  const schedule = formatEventScheduleRange(row);
  const type = getEventTypeLabelDa(row.event_type ?? '').trim();
  if (!type || schedule === '—') return schedule;
  return `${schedule} · ${type}`;
}

export function formatCompactDateTime(value: string | null | undefined): string {
  const d = parseEventDate(value ?? undefined);
  if (!d) return value?.trim() ? String(value) : '—';
  return d.toLocaleString('da-DK', { dateStyle: 'short', timeStyle: 'short' });
}

export function buildEventScheduleColumn() {
  return {
    key: 'start_date',
    header: 'Tid',
    type: 'text' as const,
    sortable: true,
    fitContent: true,
    minWidth: '148px',
    maxWidth: '320px',
    transform: (
      _value: unknown,
      row?: { start_date?: string; end_date?: string; event_type?: string }
    ) => (row ? formatEventScheduleOneLine(row) : '—'),
    eventTypeIconFrom: 'event_type',
    tooltip: (row: {
      start_date?: string;
      end_date?: string;
      event_type?: string;
    }) => formatEventScheduleTooltip(row),
  };
}

export function buildCateringNameLocationColumns() {
  return [
    {
      key: 'event_name',
      header: 'Navn',
      type: 'text' as const,
      sortable: true,
      wrap: true,
      fill: true,
    },
    {
      key: 'location',
      header: 'Lokation',
      type: 'text' as const,
      sortable: true,
      wrap: true,
      fill: true,
    },
  ];
}

export function buildCateringDualCountColumns() {
  return [
    {
      key: 'current_pending_user_count',
      header: 'Afv.',
      ...COMPACT_NUMERIC_COLUMN,
    },
    {
      key: 'approved_pending_user_count',
      header: 'Godk.',
      ...COMPACT_NUMERIC_COLUMN,
    },
  ];
}

export function buildCateringApprovedAtColumn() {
  return {
    key: 'approved_at',
    header: 'Godkendt',
    type: 'text' as const,
    sortable: true,
    fitContent: true,
    minWidth: '96px',
    maxWidth: '128px',
    transform: (value: unknown) => formatCompactDateTime(value as string),
  };
}

export function buildCateringApprovalStatusColumn() {
  return {
    key: 'approval_status',
    header: 'Status',
    type: 'component' as const,
    componentName: 'badge',
    fitContent: true,
    minWidth: '108px',
    maxWidth: '168px',
    wrap: true,
    componentInputs: (row: EventCateringApprovalListItemDTO) => {
      const status = row.approval_status;
      if (status === 'approved') return { text: 'Godkendt', color: 'success' };
      if (status === 'needs_reapproval') {
        return { text: 'Gen-godk.', color: 'warn' };
      }
      return { text: 'Afventer', color: 'default' };
    },
    sortable: true,
  };
}

export function buildCleaningParticipantCountColumn() {
  return {
    key: 'participant_count',
    header: 'Delt.',
    ...COMPACT_NUMERIC_COLUMN,
    transform: (v: number | null | undefined) => (v == null ? '' : String(v)),
  };
}

export interface CateringGroupFilterOption {
  label: string;
  value: number;
}

let groupOptionsCache$: Observable<CateringGroupFilterOption[]> | null = null;

export function loadCateringGroupFilterOptions(http: HttpClient): Observable<CateringGroupFilterOption[]> {
  if (!groupOptionsCache$) {
    groupOptionsCache$ = http
      .get<{ items: Array<{ catering_group_id?: number; name?: string }> }>(
        `${environment.apiUrl}/events/catering/groups`
      )
      .pipe(
        map((response) =>
          (response?.items || [])
            .filter((item) => !!item.catering_group_id)
            .map((item) => ({
              label: (item.name || '').trim() || 'Ukendt',
              value: item.catering_group_id as number
            }))
            .sort((a, b) => a.label.localeCompare(b.label, 'da'))
        ),
        catchError(() => of([])),
        shareReplay(1)
      );
  }

  return groupOptionsCache$;
}

export function buildCateringTableOptions(
  groupOptions: CateringGroupFilterOption[],
  params?: LoadDataParams
): any[] {
  const selectedGroup = params?.filters?.find((f) => f.column === 'catering_group')?.value ?? null;
  const includeNoParticipantsFilter = params?.filters?.find((f) => f.column === 'include_no_participants');
  const includeNoParticipants = includeNoParticipantsFilter !== undefined
    ? Boolean(includeNoParticipantsFilter.value)
    : true;

  return [
    {
      id: 'catering_group',
      label: 'Forplejningsgruppe',
      type: 'select',
      default: selectedGroup,
      options: [
        { label: 'Alle', value: null },
        ...groupOptions.map((opt) => ({ label: opt.label, value: opt.value }))
      ],
      onChange: () => {}
    },
    {
      id: 'include_no_participants',
      label: 'Vis events uden deltagere',
      type: 'toggle',
      default: includeNoParticipants,
      onChange: () => {}
    }
  ];
}
